import { useEffect, useMemo, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import AbstractShape from "@/components/AbstractShape";
import MelbiVial from "@/components/MelbiVial";
import FullPricing from "@/components/FullPricing";
import MedicationDetails from "@/components/MedicationDetails";
import { getTreatment, TREATMENTS } from "@/lib/treatments";
import useSEO from "@/lib/useSEO";
import {
  productSchema,
  breadcrumbSchema,
  medicalWebPageSchema,
  faqPageSchema,
} from "@/lib/schema";
import { goToProductIntake, PRODUCT_INTAKE_URLS } from "@/lib/intake";
import { RENEWAL_FAQS } from "@/lib/faqData";
import { PRICE_FAQS } from "@/lib/priceFaqs";


// Treatment slugs that have a dedicated OG social preview image in
// /public/og/. Anything else falls back to the site default so crawlers /
// social platforms never fetch a 404.
const OG_SLUGS = new Set([
  "energy",
  "antioxidant",
  "vitality",
  "metabolic-support",
  "weight-loss",
  "recovery",
  "skin-care",
  "nad-nasal-spray",
]);

// Regex used to segregate the "How does the Melbi process work?" FAQ item
// from the rest so it can always be rendered last in the accordion.
const HOW_IT_WORKS_RE = /how does it work/i;

export default function TreatmentDetail() {
  const { slug } = useParams();
  const t = getTreatment(slug);
  const [faqOpen, setFaqOpen] = useState("");

  useEffect(() => {
    setFaqOpen(window.location.hash === "#safety-eligibility" ? "item-safety" : "");
  }, [slug]);

  const openSafetyFaq = (e) => {
    e.preventDefault();
    setFaqOpen("item-safety");
    setTimeout(() => {
      document.getElementById("safety-eligibility")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  };


  const ogImage = t
    ? OG_SLUGS.has(t.slug)
      ? `https://melbiwellness.com/og/${t.slug}.png?v=2`
      : "https://melbiwellness.com/og/default.png?v=11"
    : undefined;

  useSEO(
    t
      ? {
          title: `Melbi ${t.name} — ${t.treatment}`,
          description: t.seoDescription || t.short,
          image: ogImage,
          url: `https://melbiwellness.com/treatments/${t.slug}`,
          jsonLd: [
            productSchema(
              { ...t, title: `${t.name} — ${t.treatment}`, metaDescription: t.seoDescription || t.short },
              t.slug,
            ),
            breadcrumbSchema([
              { name: "Home", url: "/" },
              { name: "Treatments", url: "/treatments" },
              { name: t.name, url: `/treatments/${t.slug}` },
            ]),
            medicalWebPageSchema({
              name: `${t.name} — ${t.treatment}`,
              description: t.seoDescription || t.short,
              url: `/treatments/${t.slug}`,
              offers: t.pricing?.options
                ? t.pricing.options.map((o) => ({ name: o.name, price: o.price }))
                : t.pricing?.startingAt
                  ? [{ name: t.treatment, price: t.pricing.startingAt }]
                  : [],
            }),
            faqPageSchema([
              ...(PRICE_FAQS[t.slug] ? [PRICE_FAQS[t.slug]] : []),
              { q: "What is it for?", a: t.whatItIsFor || t.short },
              ...(t.whoFor ? [{ q: "Who is it for?", a: t.whoFor }] : []),
              ...(t.faqs || []).filter((f) => !HOW_IT_WORKS_RE.test(f.q)),
              ...(t.safety?.length
                ? [{
                    q: "Who qualifies and what should I know about safety?",
                    aText: [t.safetyIntro, ...t.safety].filter(Boolean).join(" "),
                  }]
                : []),
              ...(t.faqs || [])
                .filter((f) => HOW_IT_WORKS_RE.test(f.q))
                .map((f) => ({ q: "How does the Melbi process work?", a: f.a })),
              ...RENEWAL_FAQS.map(({ q, a }) => ({ q, a })),
            ]),
          ].filter(Boolean),
        }
      : {}
  );

  // Split FAQs once per render into non-process items (rendered before the
  // safety accordion) and the "How does the Melbi process work?" item
  // (rendered last). Pre-computing avoids filtering `t.faqs` twice in JSX.
  // Called BEFORE the early return to satisfy React's rules of hooks.
  const faqs = useMemo(() => t?.faqs || [], [t]);
  const otherFaqs = useMemo(
    () => faqs.filter((f) => !HOW_IT_WORKS_RE.test(f.q)),
    [faqs],
  );
  const processFaqs = useMemo(
    () => faqs.filter((f) => HOW_IT_WORKS_RE.test(f.q)),
    [faqs],
  );

  if (!t || t.hidden) return <Navigate to="/treatments" replace />;

  // Compute "next" for the bottom card. Exclude the current slug and any
  // slugs that redirect to another canonical page (e.g. nad-nasal-spray →
  // vitality) so we never self-link.
  const REDIRECT_SLUGS = new Set(["nad-nasal-spray"]);
  const eligibleNext = TREATMENTS.filter(
    (x) => x.slug !== slug && !REDIRECT_SLUGS.has(x.slug),
  );
  // Pick the treatment that appears immediately after the current one in
  // display order, wrapping around, from the eligible list.
  const currentDisplayIdx = TREATMENTS.findIndex((x) => x.slug === slug);
  const next =
    eligibleNext.find(
      (x) => TREATMENTS.findIndex((y) => y.slug === x.slug) > currentDisplayIdx,
    ) || eligibleNext[0];

  return (
    <div data-testid={`treatment-detail-${slug}`}>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <AbstractShape variant={t.gradientClass} colors={t.blobColors} className="absolute inset-0" testId={`detail-bg-${slug}`} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-10 md:pt-24 md:pb-14">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <Link to="/treatments" className="text-sm text-[#5E5C58] hover:text-[#C96A4E] transition-colors">
                ← All treatments
              </Link>
              <p
                data-testid={`detail-eyebrow-${slug}`}
                className="mt-6 font-display text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight text-[#8A9A92] leading-tight"
              >
                {t.detailLabel || t.treatment}
              </p>
              {t.heroHeadline ? (
                (() => {
                  const [pre, ...rest] = t.heroHeadline.split(", made simple");
                  const accent = rest.length ? ", made simple" + rest.join(", made simple") : "";
                  return (
                    <h1 className="mt-2 font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight text-[#2C2B29]">
                      {pre}
                      {accent && (
                        <span className="italic text-[#C96A4E] font-normal">{accent}</span>
                      )}
                    </h1>
                  );
                })()
              ) : (
                <h1 className="mt-2 font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight text-[#2C2B29]">
                  {t.name}{" "}
                  <span className="italic text-[#C96A4E]">wellness, guided.</span>
                </h1>
              )}
              <p className="mt-5 text-[#5E5C58] text-lg leading-relaxed max-w-2xl">
                {t.heroSubheadline || t.short}
              </p>
              {t.positioning && (
                <p className="mt-4 text-[#5E5C58] text-base leading-relaxed max-w-2xl">
                  {t.positioning}
                </p>
              )}
              {t.pricing && (
                <div
                  data-testid={`detail-pricing-${slug}`}
                  className="mt-6 bg-white/70 backdrop-blur-sm border border-[#E8E5DF] rounded-2xl px-5 py-4 w-full sm:inline-block sm:w-auto"
                >
                  {t.pricing.options ? (
                    <ul className="space-y-3 sm:space-y-2">
                      {t.pricing.options.map((o) => {
                        const optInline = t.medDetails?.options?.find(
                          (m) => o.name.toLowerCase().includes(m.label.toLowerCase()) ||
                                 m.label.toLowerCase().includes(o.name.toLowerCase()),
                        )?.inline;
                        return (
                        <li
                          key={o.name}
                          className="flex flex-col"
                          data-testid={`detail-price-option-${slug}-${o.name.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                          <span className="text-[#8A9A92] font-normal text-[11px] sm:text-sm md:text-base uppercase tracking-[0.14em] shrink-0">
                            {o.name}
                          </span>
                          <span className="font-display text-lg md:text-xl font-semibold text-[#2C2B29] leading-tight">
                            <span className="text-[#C96A4E]">${o.price}</span>
                            <span>/month</span>
                            {optInline && (
                              <span
                                data-testid={`detail-price-option-concentration-${slug}-${o.name.toLowerCase().replace(/\s+/g, "-")}`}
                                className="ml-0 sm:ml-2 block sm:inline text-[11px] sm:text-xs md:text-sm font-normal text-[#5E5C58] mt-0.5 sm:mt-0"
                              >
                                {optInline}
                              </span>
                            )}
                          </span>
                          {!o.monthlyOnly && (
                            <span className="mt-0.5 text-[11px] sm:text-xs md:text-sm text-[#8A9A92] leading-snug">
                              3-month plan
                            </span>
                          )}
                        </li>
                      );})}
                    </ul>
                  ) : (
                    <>
                    <p className="font-display text-lg md:text-2xl font-semibold text-[#2C2B29] leading-tight">
                      <span className="text-[#C96A4E]">${t.pricing.startingAt}</span>
                      <span>/month</span>
                    </p>
                    {t.pricing.cardSuffix !== "" && (
                      <p className="mt-0.5 text-xs md:text-sm text-[#8A9A92] leading-snug">
                        3-month plan
                      </p>
                    )}
                    {t.medDetails?.inline && (
                      <p
                        data-testid={`detail-price-concentration-${slug}`}
                        className="mt-1 text-xs md:text-sm text-[#5E5C58] leading-snug"
                      >
                        {t.medDetails.inline}
                      </p>
                    )}
                    </>
                  )}
                  {t.pricing.note && (
                    <p
                      data-testid={`detail-price-note-${slug}`}
                      className="mt-2 text-[11px] md:text-xs text-[#5E5C58] leading-snug"
                    >
                      {t.pricing.note}
                    </p>
                  )}
                </div>
              )}
              {t.fullPricing && <FullPricing treatment={t} showRenewal />}
              {slug === "weight-loss" && (
                <Link
                  to="/treatments/glp-nad-bundles"
                  data-testid={`bundle-upsell-callout-${slug}`}
                  className="group mt-3 flex items-center justify-between gap-3 w-full sm:inline-flex sm:w-auto bg-white/70 backdrop-blur-sm border border-[#E8E5DF] hover:border-[#C96A4E]/40 rounded-2xl px-4 py-2.5 transition-colors"
                >
                  <span className="text-[13px] md:text-sm text-[#2C2B29] leading-snug">
                    <span className="block text-[11px] md:text-xs font-bold uppercase tracking-[0.12em] text-[#8A9A92]">
                      Want added vitality support?
                    </span>
                    <span className="font-semibold text-[#C96A4E]">
                      Explore Weight Management + NAD+
                    </span>
                  </span>
                  <ArrowRight
                    size={16}
                    className="shrink-0 text-[#C96A4E] transition-transform group-hover:translate-x-0.5"
                  />
                </Link>
              )}
              <div className="mt-8">
                <div className="flex flex-col sm:inline-flex gap-1">
                  <button
                    data-testid={`detail-start-now-cta-${slug}`}
                    className="inline-flex items-center justify-center bg-[#C96A4E] text-white rounded-full px-8 py-4 text-base font-semibold hover:bg-[#B35A40] transition-colors shadow-sm"
                    type="button"
                    onClick={() => goToProductIntake(slug)}
                  >
                    Start now
                    <ArrowRight size={18} className="ml-2" />
                  </button>
                  {t.safety?.length > 0 ? (
                    <a
                      href="#safety-eligibility"
                      data-testid={`detail-safety-link-${slug}`}
                      onClick={openSafetyFaq}
                      className="self-center inline-flex items-center min-h-[44px] px-3 text-sm md:text-base font-semibold text-[#2C2B29] underline decoration-[#C96A4E] decoration-2 underline-offset-4 hover:text-[#C96A4E] transition-colors"
                    >
                      Safety & Eligibility
                    </a>
                  ) : (
                    <Link
                      to="/what-to-expect"
                      className="self-center inline-flex items-center min-h-[44px] px-3 text-sm md:text-base font-semibold text-[#2C2B29] underline decoration-[#C96A4E] decoration-2 underline-offset-4 hover:text-[#C96A4E] transition-colors"
                    >
                      Safety & Eligibility
                    </Link>
                  )}
                </div>
              </div>
            </div>
            <div className="lg:col-span-5 flex items-center justify-center -space-x-20 md:-space-x-28 lg:-space-x-32">
              <div className="shrink-0">
                <MelbiVial
                  slug={t.primaryVialSlug || slug}
                  alt={`Melbi ${t.vialLabel}`}
                  testId={`detail-vial-${slug}`}
                  className="h-64 sm:h-72 md:h-96 lg:h-[26rem] w-auto vial-shadow"
                  eager
                />
              </div>
              {(t.secondaryVialSlug || t.heroSecondaryVialSlug) && (
                <div className="shrink-0">
                  <MelbiVial
                    slug={t.secondaryVialSlug || t.heroSecondaryVialSlug}
                    alt={t.secondaryVialAlt || `Melbi ${t.secondaryVialSlug || t.heroSecondaryVialSlug}`}
                    testId={`detail-vial-secondary-${slug}`}
                    className="h-64 sm:h-72 md:h-96 lg:h-[26rem] w-auto vial-shadow"
                    eager
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* BENEFITS + WHO IT'S FOR */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 md:pt-6 pb-10 md:pb-14">
        {t.medDetails && (
          <div className="mb-8 md:mb-10">
            <MedicationDetails treatment={t} />
          </div>
        )}
        <div className="grid lg:grid-cols-2 gap-12">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A9A92]">What is it for?</p>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-semibold tracking-tight text-[#2C2B29] leading-tight">
              {t.name}
            </h2>
            {t.whatItIsForIntro && t.whatItIsForIntro.length > 0 ? (
              <div
                data-testid={`what-it-is-for-intro-${slug}`}
                className="mt-5 space-y-4 text-[#2C2B29] text-lg leading-relaxed"
              >
                {t.whatItIsForIntro.map((para) => (
                  <p key={para}>{para}</p>
                ))}
              </div>
            ) : (
              <p
                data-testid={`what-it-is-for-${slug}`}
                className="mt-5 text-[#2C2B29] text-lg leading-relaxed"
              >
                {t.whatItIsFor || t.short}
              </p>
            )}
            <ul className="mt-8 space-y-3">
              {t.benefits.map((b) => (
                <li key={b} className="flex items-start gap-3 text-[#2C2B29]">
                  <span className="mt-0.5 w-9 h-9 rounded-full bg-[#F3F1EC] flex items-center justify-center shrink-0">
                    <Check size={18} className="text-[#C96A4E]" />
                  </span>
                  <span className="font-medium leading-relaxed pt-1.5">{b}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-[#F3F1EC]/60 rounded-3xl p-6 md:p-8 border border-[#E8E5DF]">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A9A92]">What to expect</p>
            <h3 className="mt-3 font-display text-2xl font-semibold tracking-tight text-[#2C2B29]">A guided, clinician reviewed flow.</h3>
            <div className="mt-6 grid grid-cols-2 gap-3">
              {[
                "Choose your goal",
                "Complete intake",
                "Clinician review",
                "Pharmacy fulfillment",
              ].map((step, i) => (
                <div key={step} className="bg-white rounded-2xl p-4 border border-[#E8E5DF]">
                  <p className="text-xs text-[#8A9A92] font-bold tracking-widest">0{i + 1}</p>
                  <p className="mt-1 text-sm font-semibold text-[#2C2B29]">{step}</p>
                </div>
              ))}
            </div>
            <Link
              to="/what-to-expect"
              data-testid={`what-to-expect-learn-more-${slug}`}
              className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#C96A4E] hover:text-[#B35A40] hover:gap-2 hover:underline underline-offset-4 transition-all"
            >
              Learn more about how Melbi works
              <ArrowRight size={14} strokeWidth={2.5} />
            </Link>
            {PRODUCT_INTAKE_URLS[slug] && (
              <div>
                <button
                  type="button"
                  data-testid={`what-to-expect-start-now-${slug}`}
                  onClick={() => goToProductIntake(slug)}
                  className="mt-5 inline-flex items-center justify-center bg-[#C96A4E] text-white rounded-full px-6 py-3 text-sm font-semibold hover:bg-[#B35A40] transition-colors shadow-sm"
                >
                  Start Now
                  <ArrowRight size={16} className="ml-2" />
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 md:pb-24">
        <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-[#2C2B29] text-center">
          Common questions
        </h2>
        <div className="mt-10">
          <Accordion type="single" collapsible value={faqOpen} onValueChange={setFaqOpen} className="space-y-3" data-testid={`treatment-faq-${slug}`}>
            {PRICE_FAQS[slug] && (
              <AccordionItem
                value="item-price"
                data-testid={`treatment-faq-price-${slug}`}
                className="bg-white rounded-2xl border border-[#E8E5DF] px-5"
              >
                <AccordionTrigger className="text-left font-display text-base md:text-lg font-semibold text-[#2C2B29] hover:no-underline py-5">
                  {PRICE_FAQS[slug].q}
                </AccordionTrigger>
                <AccordionContent className="text-[#5E5C58] leading-relaxed pb-5 text-sm md:text-base">
                  {PRICE_FAQS[slug].a}
                </AccordionContent>
              </AccordionItem>
            )}
            <AccordionItem
              value="item-whatfor"
              data-testid={`treatment-faq-whatfor-${slug}`}
              className="bg-white rounded-2xl border border-[#E8E5DF] px-5"
            >
              <AccordionTrigger className="text-left font-display text-base md:text-lg font-semibold text-[#2C2B29] hover:no-underline py-5">
                What is it for?
              </AccordionTrigger>
              <AccordionContent className="text-[#5E5C58] leading-relaxed pb-5 text-sm md:text-base">
                {t.whatItIsFor || t.short}
              </AccordionContent>
            </AccordionItem>
            {t.whoFor && (
              <AccordionItem
                value="item-whofor"
                data-testid={`treatment-faq-whofor-${slug}`}
                className="bg-white rounded-2xl border border-[#E8E5DF] px-5"
              >
                <AccordionTrigger className="text-left font-display text-base md:text-lg font-semibold text-[#2C2B29] hover:no-underline py-5">
                  Who is it for?
                </AccordionTrigger>
                <AccordionContent className="text-[#5E5C58] leading-relaxed pb-5 text-sm md:text-base">
                  {t.whoFor}
                </AccordionContent>
              </AccordionItem>
            )}
            {/* Non "How does it work" FAQs first */}
            {otherFaqs.map((f) => (
              <AccordionItem
                key={f.q}
                value={`item-other-${f.q}`}
                className="bg-white rounded-2xl border border-[#E8E5DF] px-5"
              >
                <AccordionTrigger className="text-left font-display text-base md:text-lg font-semibold text-[#2C2B29] hover:no-underline py-5">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#5E5C58] leading-relaxed pb-5 text-sm md:text-base">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
            {/* Safety & eligibility — pulled from category safety list */}
            {t.safety && t.safety.length > 0 && (
              <AccordionItem
                value="item-safety"
                id="safety-eligibility"
                data-testid={`treatment-faq-safety-${slug}`}
                className="scroll-mt-24 md:scroll-mt-28 bg-white rounded-2xl border border-[#E8E5DF] px-5"
              >
                <AccordionTrigger className="text-left font-display text-base md:text-lg font-semibold text-[#2C2B29] hover:no-underline py-5">
                  Who qualifies and what should I know about safety?
                </AccordionTrigger>
                <AccordionContent className="text-[#5E5C58] leading-relaxed pb-5 text-sm md:text-base">
                  {t.safetyIntro && (
                    <p
                      data-testid={`treatment-faq-safety-intro-${slug}`}
                      className="mb-4"
                    >
                      {t.safetyIntro}
                    </p>
                  )}
                  <ul className="space-y-2 list-disc pl-5">
                    {t.safety.map((s) => (
                      <li key={s} className="leading-relaxed">{s}</li>
                    ))}
                  </ul>
                </AccordionContent>
              </AccordionItem>
            )}
            {/* "How does the Melbi process work?" always last */}
            {processFaqs.map((f) => (
              <AccordionItem
                key={f.q}
                value={`item-process-${f.q}`}
                data-testid={`treatment-faq-process-${slug}`}
                className="bg-white rounded-2xl border border-[#E8E5DF] px-5"
              >
                <AccordionTrigger className="text-left font-display text-base md:text-lg font-semibold text-[#2C2B29] hover:no-underline py-5">
                  How does the Melbi process work?
                </AccordionTrigger>
                <AccordionContent className="text-[#5E5C58] leading-relaxed pb-5 text-sm md:text-base">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
            {RENEWAL_FAQS.map((f, i) => (
              <AccordionItem
                key={f.q}
                value={`item-renewal-${i}`}
                data-testid={`treatment-faq-renewal-${i}-${slug}`}
                className="bg-white rounded-2xl border border-[#E8E5DF] px-5"
              >
                <AccordionTrigger className="text-left font-display text-base md:text-lg font-semibold text-[#2C2B29] hover:no-underline py-5">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#5E5C58] leading-relaxed pb-5 text-sm md:text-base">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* NEXT TREATMENT */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 md:pb-20">
        <Link
          to={`/treatments/${next.slug}`}
          className="block group bg-white rounded-3xl border border-[#E8E5DF] overflow-hidden hover:border-[#C96A4E]/40 transition-all duration-300"
        >
          <div className="grid md:grid-cols-2">
            <div className="relative h-56 md:h-auto overflow-hidden">
              <AbstractShape
                variant={next.gradientClass}
                colors={next.blobColors}
                className="absolute inset-0"
                testId={`next-shape-${next.slug}`}
              />
              <div className="relative z-10 flex items-center justify-center h-full py-8 -space-x-14 md:-space-x-20">
                <MelbiVial
                  slug={next.primaryVialSlug || next.slug}
                  alt={`Melbi ${next.vialLabel}`}
                  testId={`next-vial-${next.slug}`}
                  className="h-44 md:h-64 w-auto vial-shadow transition-transform duration-300 group-hover:scale-105"
                />
                {next.secondaryVialSlug && (
                  <MelbiVial
                    slug={next.secondaryVialSlug}
                    alt={`Melbi ${next.secondaryVialSlug}`}
                    testId={`next-vial-secondary-${next.slug}`}
                    className="h-44 md:h-64 w-auto vial-shadow transition-transform duration-300 group-hover:scale-105"
                  />
                )}
              </div>
            </div>
            <div className="p-6 md:p-10 flex flex-col justify-center">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A9A92]">Next category</p>
              <h3 className="mt-2 font-display text-3xl font-semibold tracking-tight text-[#2C2B29]">
                {next.name}
              </h3>
              <p className="mt-3 text-[#5E5C58] leading-relaxed">{next.short}</p>
              <span className="mt-5 inline-flex items-center text-sm font-semibold text-[#C96A4E] group-hover:gap-2 gap-1 transition-all">
                Explore {next.name} <ArrowRight size={14} />
              </span>
            </div>
          </div>
        </Link>
      </section>

      {/* DISCLOSURES */}
      <section
        data-testid={`disclosures-${slug}`}
        className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 md:pb-20"
      >
        <div className="bg-[#F3F1EC]/60 rounded-3xl border border-[#E8E5DF] p-6 md:p-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A9A92]">
            Important disclosures
          </p>
          <div className="mt-5 space-y-4 text-xs md:text-sm text-[#5E5C58] leading-relaxed">
            {slug === "weight-loss" && (
              <p>
                <span className="font-semibold text-[#2C2B29]">GLP-1 compounded medication disclosure:</span>{" "}
                Compounded semaglutide and compounded tirzepatide are not FDA-approved.
                They are not reviewed or approved by the FDA for safety, effectiveness,
                or quality. Availability depends on clinical appropriateness, pharmacy
                availability, applicable law, and current regulatory requirements.
              </p>
            )}
            <p>
              <span className="font-semibold text-[#2C2B29]">Compounded medication disclosure:</span>{" "}
              Compounded medications are not FDA-approved. The FDA does not review or
              approve compounded medications for safety, effectiveness, or quality
              before they are marketed.
            </p>
            <p>
              <span className="font-semibold text-[#2C2B29]">FDA statement:</span>{" "}
              Statements on this page have not been evaluated by the Food and Drug
              Administration. This product is not intended to diagnose, treat, cure, or
              prevent any disease.
            </p>
            <p>
              <span className="font-semibold text-[#2C2B29]">Prescription and provider:</span>{" "}
              Melbi connects patients with licensed healthcare providers who may
              prescribe medication when clinically appropriate. Prescription medication
              is only available if prescribed after an online consultation and clinical
              review by a licensed healthcare provider.
            </p>
            <p>
              <span className="font-semibold text-[#2C2B29]">Pharmacy:</span>{" "}
              If prescribed, medication is dispensed by a state-licensed pharmacy and
              shipped directly to the patient.
            </p>
            <p>
              <span className="font-semibold text-[#2C2B29]">Subscription:</span>{" "}
              Plans are offered as subscription services and may be canceled at any
              time, subject to the terms provided at checkout.
            </p>
            <p>
              <span className="font-semibold text-[#2C2B29]">Product image:</span>{" "}
              Actual product packaging may appear differently than shown.
            </p>
          </div>
        </div>
      </section>

      {/* /DISCLOSURES */}
    </div>
  );
}
